/**
 * JournalEntry Component
 *
 * Displays a single note entry in the journal view.
 * Shows timestamp header and content with inline editing.
 */

import { useState, useCallback } from 'react';
import { Edit2, Trash2, Check, X, Star } from 'lucide-react';
import { RichTextEditor, RichTextViewer } from '@/components/RichTextEditor';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/utils/dates';
import { cn } from '@/utils/cn';
import type { NoteItem } from '@/types';

// ============================================
// TYPES
// ============================================

export interface JournalEntryProps {
  /** The note item to display */
  note: NoteItem;
  /** Handler for saving edits */
  onSave: (note: NoteItem) => void;
  /** Handler for deleting */
  onDelete: (note: NoteItem) => void;
  /** Handler for toggling favorite */
  onToggleFavorite?: (note: NoteItem) => void;
}

// ============================================
// COMPONENT
// ============================================

export function JournalEntry({ note, onSave, onDelete, onToggleFavorite }: JournalEntryProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(note.content);

  const { time } = formatDateTime(note.createdAt);

  // Handle save
  const handleSave = useCallback(() => {
    if (editContent !== note.content) {
      // Generate plain text preview
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = editContent;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      const plainTextPreview = plainText.slice(0, 200);

      onSave({
        ...note,
        content: editContent,
        plainTextPreview,
      });
    }
    setIsEditing(false);
  }, [editContent, note, onSave]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setEditContent(note.content);
    setIsEditing(false);
  }, [note.content]);

  // Handle delete - no confirmation for quick journal workflow
  const handleDelete = useCallback(() => {
    onDelete(note);
  }, [note, onDelete]);

  return (
    <div
      className={cn(
        'group relative pl-4 border-l-2 border-gray-200',
        'hover:border-brand-300 transition-colors'
      )}
    >
      {/* Timestamp header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <time className="text-xs font-medium text-gray-500">{time}</time>
          {note.isFavorite && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />}
        </div>

        {/* Actions - shown on hover or when editing */}
        <div
          className={cn(
            'flex items-center gap-1',
            !isEditing && 'opacity-0 group-hover:opacity-100 transition-opacity'
          )}
        >
          {isEditing ? (
            <>
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                <X className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSave}>
                <Check className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              {onToggleFavorite && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onToggleFavorite(note)}
                  className="h-7 w-7 p-0"
                >
                  <Star
                    className={cn(
                      'h-3.5 w-3.5',
                      note.isFavorite && 'fill-amber-400 text-amber-400'
                    )}
                  />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="h-7 w-7 p-0"
              >
                <Edit2 className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDelete}
                className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      {isEditing ? (
        <RichTextEditor
          content={editContent}
          onChange={setEditContent}
          placeholder="Write your note..."
          minHeight="100px"
          autofocus
        />
      ) : (
        <div
          className="cursor-pointer hover:bg-gray-50 rounded -m-2 p-2 transition-colors"
          onClick={() => setIsEditing(true)}
        >
          <RichTextViewer content={note.content} />
        </div>
      )}
    </div>
  );
}
